import React from 'react';
import useAuthContext from '../../hooks/useAuthContext';
import { Title, Loader, ReloadIconButton, TablePaginationWrapper } from '../../elements';
import { useConfirmation } from '../../hooks/ConfirmationService';
import { DEFAULT_ROWS_PAGE_OPTIONS, DEFAULT_ROWS_PER_PAGE_MIDDLE, LoaderSize } from '../../constants/constants';
import { notifications, extractApiErrorMessage } from '../../utils';
import {
  Grid,
  Icon,
  Paper,
  TableRow,
  TableBody,
  TableCell,
  Table,
  Tooltip,
  IconButton,
  TableHead,
  TableContainer,
  Chip,
  Box,
  Typography
} from '@material-ui/core';
import { useStyles } from './styles';
import { useTranslation } from 'react-i18next';
import { CreateDiseaseModal } from '../../components';
import { LocationStable } from '../../models/locations';
import UserRole from '../../constants/user-role';
import { ApiService } from '../../hooks';
import {
  HealthSystemDALModelDiseasesStableColour,
  HealthSystemDomainDiseasesDiseaseViewModel
} from '../../api/HealthSystemApi';

const FarmDiseases: React.FC<any> = () => {
  const classes = useStyles();
  const apiService = new ApiService();
  const { state } = useAuthContext();
  const { t } = useTranslation();
  const confirmDialog = useConfirmation();

  const [diseases, setDiseases] = React.useState<HealthSystemDomainDiseasesDiseaseViewModel[]>([]);
  const [isBusy, setIsBusy] = React.useState(false);
  const [loaded, setLoaded] = React.useState(false);
  const [createDiseaseModalOpened, setCreateDiseaseModalOpened] = React.useState(false);

  const loadFarmDiseases = async () => {
    setIsBusy(true);

    const searchPage = page === 0 ? 1 : page + 1;

    apiService.api.diseases
      .diseasesDiseasesSearch({
        Page: searchPage,
        Size: rowsPerPage
      })
      .then((result) => {
        setDiseases(result.data.data);
        setTotal(result.data.total);
      })
      .catch((e: unknown) => {
        notifications.error(extractApiErrorMessage(e, { fallback: t('f-com-api-error') }));
      })
      .finally(() => {
        setIsBusy(false);
        setLoaded(true);
      });
  };

  React.useEffect(() => {
    if (loaded === false) {
      const loadData = async () => loadFarmDiseases();
      loadData();
    }
  }, [loaded]);

  // pagination
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(DEFAULT_ROWS_PER_PAGE_MIDDLE);
  const [total, setTotal] = React.useState(0);

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
    setLoaded(false);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
    setLoaded(false);
  };
  // * pagination

  const handleRemoveDisease = (disease: HealthSystemDomainDiseasesDiseaseViewModel) => {
    confirmDialog({
      dialogId: 'delete-disease-dialog',
      title: t('f-diseases-del'),
      message: t('f-diseases-del-confirm', { disease: disease.name }),
      alertSeverity: 'warning'
    }).then(() => {
      apiService.api.diseases
        .diseasesRemoveDisease({ id: disease.id })
        .then(() => {
          notifications.success(t('f-diseases-del-ok'));
          setLoaded(false);
        })
        .catch((e: unknown) => {
          notifications.error(extractApiErrorMessage(e, { fallback: t('f-com-api-error') }));
        });
    });
  };

  const handleCreateDiseaseClose = (result: boolean) => {
    if (result) {
      setLoaded(false);
    }
    setCreateDiseaseModalOpened(false);
  };

  const renderStableColors = (array: HealthSystemDALModelDiseasesStableColour[]) => {
    if (!array || !array.length) {
      return null;
    }
    return array.map((value, index) => (
      <Box display="flex" key={index} mt={index < 1 ? 0 : 1}>
        <Box height={20} width={20} borderRadius={3} bgcolor={value.colour} />
        <Box pl={1}>
          <Typography variant={'caption'}>{LocationStable[value.stable]}</Typography>
        </Box>
      </Box>
    ));
  };

  return (
    <>
      <Grid container spacing={1}>
        <Grid item xs={12} md={12}>
          {/* title & spinner */}
          <Grid container direction="row" className={classes.paperHeader}>
            <Grid item container md={6} xs={10} spacing={1}>
              <Grid item>
                <Icon fontSize="large" color="primary">
                  {'sick'}
                </Icon>
              </Grid>
              <Grid item>
                <Title>{t('f-com-diseases')}</Title>
              </Grid>
              <Grid item>
                <Chip label={total} variant="outlined" />
              </Grid>
            </Grid>

            <Grid item container className={classes.justifyRight} md={6} xs={12}>
              <Grid item>
                <ReloadIconButton action={() => setLoaded(false)} />
              </Grid>

              <Grid item>
                <Tooltip title={t('f-com-create-new')} arrow={true}>
                  <IconButton
                    disabled={state.role === UserRole.user}
                    id="createSupplyBtn"
                    className={classes.withMarginLeft}
                    color="primary"
                    size="small"
                    onClick={() => setCreateDiseaseModalOpened(true)}
                  >
                    <Icon>{'add_circle'}</Icon>
                  </IconButton>
                </Tooltip>
              </Grid>
            </Grid>
          </Grid>

          <Loader isLoading={isBusy} size={LoaderSize.DEFAULT_SIZE} />

          {/* data table */}
          {loaded && (
            <Grid container spacing={1}>
              <Grid item xs={12}>
                <TablePaginationWrapper
                  rowsPerPageOptions={DEFAULT_ROWS_PAGE_OPTIONS}
                  count={total}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                >
                  <TableContainer component={Paper} elevation={1} className={classes.mt2}>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>{t('f-com-name')}</TableCell>
                          <TableCell>{t('f-com-colour')}</TableCell>
                          <TableCell>{t('f-com-supply')}</TableCell>
                          <TableCell>{t('f-com-treat-num-days')}</TableCell>
                          <TableCell>{t('f-com-dose')}</TableCell>
                          <TableCell padding="checkbox" />
                        </TableRow>
                      </TableHead>

                      <TableBody>
                        {diseases.map((row) => (
                          <TableRow key={row.id} hover>
                            <TableCell>{row.name}</TableCell>
                            <TableCell>{renderStableColors(row.stableColours)}</TableCell>
                            <TableCell>{row.medicalSupply.number}</TableCell>
                            <TableCell>{row.medicalSupply.daysToTreat}</TableCell>
                            <TableCell>{row.medicalSupply.dose.toFixed(2) || '-'}</TableCell>
                            <TableCell>
                              <Tooltip arrow title={t('f-diseases-del')}>
                                <IconButton color="secondary" size="small" onClick={() => handleRemoveDisease(row)}>
                                  <Icon>{'delete'}</Icon>
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </TablePaginationWrapper>
              </Grid>
            </Grid>
          )}
        </Grid>
      </Grid>

      <CreateDiseaseModal opened={createDiseaseModalOpened} onModalAction={handleCreateDiseaseClose} />
    </>
  );
};

export default FarmDiseases;
